import React, { useState } from 'react';
import { Conversion } from '../../types';
import EditionCard, { conversionToEdition } from '../feed/EditionCard';
import TranscriptModal from '../conversion/TranscriptModal';
import ReportConversionModal from '../conversion/ReportConversionModal';
import { conversionsApi, downloadConversion } from '../../api/conversions';
import { Button, Spinner } from '../common';

interface LibraryListProps {
  conversions: Conversion[];
  loading?: boolean;
  total?: number;
  currentPage?: number;
  pageSize?: number;
  playingId?: string | null;
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onPageChange?: (page: number) => void;
  onPlay?: (conversion: Conversion) => void;
  onDelete?: (id: string) => Promise<void>;
  onToggleSelect?: (id: string) => void;
  /** Override download behaviour (defaults to fetching the MP3 via the API). */
  onDownload?: (conversion: Conversion) => Promise<unknown> | void;
  /** Called after a conversion has been reported, so the caller can refresh it. */
  onReported?: (id: string) => void;
  /** Called after a renewal has been queued, so the caller can refresh it. */
  onRenewed?: (id: string) => void;
  /** Called when a renewal request fails, with a message to show. */
  onRenewError?: (message: string) => void;
}

export default function LibraryList({
  conversions,
  loading = false,
  total = 0,
  currentPage = 1,
  pageSize = 10,
  playingId = null,
  selectionMode = false,
  selectedIds,
  onPageChange,
  onPlay,
  onDelete,
  onToggleSelect,
  onDownload,
  onReported,
  onRenewed,
  onRenewError,
}: LibraryListProps) {
  const [transcriptFor, setTranscriptFor] = useState<Conversion | null>(null);
  const [reportFor, setReportFor] = useState<Conversion | null>(null);

  const readyEditions = conversions.filter(
    (c) => c.status === 'completed' || c.status === 'expired'
  );

  const totalPages = Math.ceil(total / pageSize);
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;

  if (loading && readyEditions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!loading && readyEditions.length === 0) {
    return (
      <div className="text-center py-12 bg-card border border-border rounded-sm">
        <svg
          className="mx-auto h-12 w-12 text-muted-foreground opacity-50"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
        </svg>
        <h3 className="mt-6 font-display text-xl font-medium text-foreground">No conversions yet</h3>
        <p className="mt-2 font-body text-muted-foreground">
          Convert your first piece of text to begin your collection.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {readyEditions.map((conversion, idx) => {
        const isSelected = selectedIds?.has(conversion.id) ?? false;
        const card = (
          <EditionCard
            edition={conversionToEdition(conversion, readyEditions.length - idx)}
            isActive={playingId === conversion.id}
            onPlay={
              selectionMode || !onPlay
                ? undefined
                : (id) => {
                    const c = readyEditions.find((x) => x.id === id);
                    if (c) onPlay(c);
                  }
            }
            onDelete={
              selectionMode || !onDelete
                ? undefined
                : (id) => {
                    void onDelete(id);
                  }
            }
            onViewTranscript={
              selectionMode
                ? undefined
                : (id) => {
                    const c = readyEditions.find((x) => x.id === id);
                    if (c) setTranscriptFor(c);
                  }
            }
            onDownload={
              selectionMode
                ? undefined
                : (id) => {
                    const c = readyEditions.find((x) => x.id === id);
                    if (!c) return;
                    return onDownload
                      ? onDownload(c)
                      : downloadConversion(c.id, c.title);
                  }
            }
            onReport={
              selectionMode
                ? undefined
                : (id) => {
                    const c = readyEditions.find((x) => x.id === id);
                    if (c) setReportFor(c);
                  }
            }
            onRenew={
              selectionMode
                ? undefined
                : async (id) => {
                    try {
                      await conversionsApi.renewConversion(id);
                      onRenewed?.(id);
                    } catch (err) {
                      onRenewError?.(err instanceof Error ? err.message : 'Failed to renew conversion');
                    }
                  }
            }
          />
        );

        if (!selectionMode) {
          return <div key={conversion.id}>{card}</div>;
        }

        return (
          <label
            key={conversion.id}
            className={`flex items-start gap-3 p-2 border rounded-sm cursor-pointer transition-colors ${
              isSelected
                ? 'border-primary/60 bg-primary/5'
                : 'border-transparent hover:bg-muted/40'
            }`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect?.(conversion.id)}
              className="mt-4 ml-1 w-4 h-4"
            />
            <div className="flex-1 min-w-0">{card}</div>
          </label>
        );
      })}

      {totalPages > 1 && onPageChange && (
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <p className="font-mono text-xs tracking-wide uppercase text-muted-foreground">
            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, total)} of {total}
          </p>

          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={!hasPrevious || loading}
            >
              Previous
            </Button>
            <span className="font-mono text-xs tracking-wide uppercase text-muted-foreground">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={!hasNext || loading}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <TranscriptModal
        isOpen={transcriptFor !== null}
        onClose={() => setTranscriptFor(null)}
        title={transcriptFor ? `Transcript: ${transcriptFor.title}` : ''}
        transcript={transcriptFor?.transcript ?? null}
      />

      <ReportConversionModal
        isOpen={reportFor !== null}
        conversionId={reportFor?.id ?? null}
        conversionTitle={reportFor?.title}
        onClose={() => setReportFor(null)}
        onReported={(report) => onReported?.(report.conversionId)}
      />
    </div>
  );
}
